import { msg } from '@lingui/core/macro';
import { Plural, useLingui } from '@lingui/react/macro';
import { format } from 'date-fns';
import { Ban, Check, X } from 'lucide-react';
import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { upsertChampionshipTeamApplication } from 'modules/teams/store/championship-teams/actions';
import { fetchChampionshipTeams } from 'modules/championship/store/championship/actions';
import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { trackEvent } from 'shared/analytics';
import { AppDispatch } from 'store/store';
import UDIconButton from 'ud-ui/components/icon-button';
import UDText from 'ud-ui/components/text';
import { AnnouncedTeamStatus } from 'modules/teams/domain/enums/announced-team-status';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { getApplicationLimitMessage, isApplicationLimitExceeded } from 'modules/teams/domain/utils/application-limit-error';
import { describeApplicationSaveError } from 'modules/teams/domain/utils/application-save-error';
import { AnnouncedTeam } from 'modules/teams/domain/interfaces/announced-team';
import ChampionshipTeamCardHeader from '../team-card/championship-team-card-header';
import { CardContainer } from '../team-card/styles';

type ChampionshipTeamCardProps = {
  championshipTeam: AnnouncedTeam;
  isUpdating?: boolean;
  championshipId: number;
};

const ChampionshipTeamCard = ({ championshipTeam, isUpdating, championshipId }: ChampionshipTeamCardProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const combinedUpdating = isUpdating || isSubmitting;

  const settings = useSelector(championshipSettingsSelector(championshipId));
  const maxPlayers = useMemo(() => {
    return settings?.applicationMaxPlayers ?? 28;
  }, [settings?.applicationMaxPlayers]);

  const handleCardClick = useCallback(() => {
    navigate(`${championshipTeam.id}`);
  }, [championshipTeam.id, navigate]);

  const handleStatusChange = useCallback(
    (status: AnnouncedTeamStatus) => {
      if (championshipTeam.applications.length === 0) {
        toast.error(t`Необходимо добавить участников в команду.`);
        return;
      }

      // Pre-validate max allowed players in a team before accepting the whole team
      if (status === AnnouncedTeamStatus.ACCEPTED) {
        const playersCount = championshipTeam.applications.length;
        if (typeof maxPlayers === 'number' && playersCount > maxPlayers) {
          toast.error(
            t`Превышено допустимое количество игроков в команде. По условиям турнира максимальное количество игроков в команде: ${maxPlayers}. При заявлении команды все игроки, находящиеся в ней, будут заявлены. Для заявления конкретных игроков перейдите в заявку команды, кликнув на неё.`,
            { autoClose: 1000 * 45 },
          );
          return;
        }
      }

      // Заявление команды меняет статус всему составу, но событие должно посчитать только
      // тех, у кого он реально сменился: у уже заявленных игроков смены нет.
      const newlyAccepted =
        status === AnnouncedTeamStatus.ACCEPTED
          ? championshipTeam.applications.filter((application) => application.status !== ApplicationStatus.ACCEPTED).length
          : 0;

      setIsSubmitting(true);
      return dispatch(
        upsertChampionshipTeamApplication({
          teamId: championshipTeam.id,
          status,
          championshipId,
        }),
      )
        .unwrap()
        .then((result) => {
          StepperMainTool.setByPath('stepper:main:applicationTeam', true);
          trackEvent('Team Application Submitted', {
            championship_id: championshipId,
            team_id: championshipTeam.id,
            flow: 'v1',
            source: 'team-card',
          });
          if (result?.status === AnnouncedTeamStatus.ACCEPTED) {
            if (newlyAccepted > 0) {
              trackEvent('Player Application Accepted', {
                championship_id: championshipId,
                team_id: championshipTeam.id,
                flow: 'v1',
                source: 'team-card',
                players_count: newlyAccepted,
              });
            }
            toast.success(t`Все игроки команды успешно заявлены.`);
          } else if (result?.status === AnnouncedTeamStatus.DECLINED) {
            toast.success(t`Все заявки игроков команда успешно отклонены.`);
          } else {
            toast.success(t`Команда возвращена в незаявленные.`);
          }
          dispatch(fetchChampionshipTeams(championshipId));
        })
        .catch((error: unknown) => {
          if (isApplicationLimitExceeded(error)) {
            toast.error(getApplicationLimitMessage(maxPlayers, 'team'), { autoClose: 1000 * 45 });
          } else toast.error(describeApplicationSaveError(error, msg`Не удалось изменить статус всех игроков команды`));
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    // Не только `.length`: событие считает заявки по статусу, значит зависит от самого массива.
    [championshipTeam.applications, championshipTeam.id, dispatch, championshipId, maxPlayers, t],
  );

  const canAccept = championshipTeam.status !== AnnouncedTeamStatus.ACCEPTED;
  const canDecline = championshipTeam.status !== AnnouncedTeamStatus.DECLINED;
  const canCancel = championshipTeam.status === AnnouncedTeamStatus.DECLINED;

  const handleActionClick = useCallback(
    (status: AnnouncedTeamStatus) => (event: React.MouseEvent) => {
      // клик по всей карточке уводит в заявку команды — кнопке это не нужно
      event.stopPropagation();
      return handleStatusChange(status);
    },
    [handleStatusChange],
  );

  // Кнопка «Заявить» показывается только для не-заявленных команд (`canAccept`),
  // а из любого такого статуса сначала нужно выбрать состав — поэтому вместо
  // смены статуса всегда открываем заявку команды.
  const handleAcceptClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      handleCardClick();
    },
    [handleCardClick],
  );

  const applicationDate = championshipTeam.applicationDate ? new Date(championshipTeam.applicationDate) : new Date();
  const count = championshipTeam.applications.length;

  const bodyComponent = (
    <div className="flex items-center justify-between" style={{ flexBasis: '50%' }}>
      <UDText type="subhead" style={{ flexBasis: '30%' }}>
        {format(applicationDate, 'dd.MM.yyyy')}
      </UDText>
      <div className="flex grow items-center justify-between">
        <UDText type="subhead" className="team-players-count">
          <Plural value={count} one="# игрок" few="# игрока" many="# игроков" other="# игрока" />
        </UDText>
        <div className="interactive flex items-center justify-end" style={{ width: 92, minWidth: 92, gap: 20 }}>
          {canAccept && (
            <UDIconButton
              icon={<Check />}
              variant="success"
              tooltip={t`Заявить`}
              disabled={combinedUpdating}
              onClick={handleAcceptClick}
            />
          )}
          {canDecline && (
            <UDIconButton
              icon={<Ban />}
              variant="danger"
              tooltip={t`Отклонить`}
              disabled={combinedUpdating}
              onClick={handleActionClick(AnnouncedTeamStatus.DECLINED)}
            />
          )}
          {canCancel && (
            <UDIconButton
              icon={<X />}
              variant="default"
              tooltip={t`Вернуть в незаявленные`}
              disabled={combinedUpdating}
              onClick={handleActionClick(AnnouncedTeamStatus.PENDING)}
            />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <CardContainer className="mb-2 last:mb-0" onClick={handleCardClick}>
      <ChampionshipTeamCardHeader
        isExpanded={false}
        containerProps={{ style: { cursor: 'pointer' } }}
        teamName={championshipTeam.name}
        shortName={championshipTeam.shortName}
        teamLogo={championshipTeam.logo?.url}
        actions={bodyComponent}
      />
    </CardContainer>
  );
};

export default ChampionshipTeamCard;
